import { BrowserModule } from '@angular/platform-browser';
import { ApplicationRef, DoBootstrap, Injector, NgModule, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideFirebaseApp, initializeApp, getApp } from '@angular/fire/app';
import { getFunctions, provideFunctions, connectFunctionsEmulator } from '@angular/fire/functions';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { AppComponent } from './app.component';
import { NgxsModule } from '@ngxs/store';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsStoragePluginModule, STORAGE_ENGINE } from '@ngxs/storage-plugin';
import { HttpLoaderFactory } from 'projects/web/src/app/TranslateHttpLoader';
import { PuzzleGameState } from 'projects/tools/src/lib/store/puzzle-game.state';
import { PuzzleIDState } from 'projects/tools/src/lib/store/puzzle-id.state';
import { PuzzleZoomState } from 'projects/tools/src/lib/store/puzzle-zoom.state';
import { PuzzleCookieConsentState } from 'projects/tools/src/lib/store/puzzle-analytics-consent.state';
import { PuzzleFrameworkConfigState } from 'projects/tools/src/lib/store/puzzle-framework-config.state';
import { SwedishGameState } from 'projects/tools/src/lib/crossword-module/store/swedish-game.state';
import { RouterModule, Router } from '@angular/router';
import { PuzzleTimeRecordState } from 'projects/tools/src/lib/store/puzzle-time-record.state';
import { environment } from '../environments/environment';
import { ImagePuzzleGameState } from 'projects/tools/src/lib/image-puzzle-module/store/image-puzzle-game.state';
import { SudokuGameState } from '../../../tools/src/lib/sudoku-module/store/sudoku-game.state';
import { HashiGameState } from '../../../tools/src/lib/hashi-module/store/hashi-game.state';
import { PuzzleNotesModeState } from '../../../tools/src/lib/store/puzzle-notes-mode.state';
import { WordSearchPuzzleState } from '../../../tools/src/lib/word-search-puzzle-module/store/word-search-puzzle.state';
import { v1 as uuidv1 } from 'uuid';
import { PuzzleStorageEngine } from './puzzle-storage-engine.service';
import { QuizGameState } from '../../../tools/src/lib/quiz-module/store/quiz-game.state';
import { createCustomElement } from '@angular/elements';
import { FullscreenOverlayContainer, OverlayContainer } from '@angular/cdk/overlay';
import { LegacySwedishGameState } from '../../../tools/src/lib/swedish-module/store/legacy-swedish-game.state';
import { LeaderboardState } from 'projects/tools/src/lib/shared-module/leaderboard/store/leaderboard.state';
import { LeaderboardNicknameState } from 'projects/tools/src/lib/shared-module/leaderboard/store/leaderboard-nickname.state';
import { FeedbackState } from 'projects/tools/src/lib/shared-module/feedback/store/feedback.state';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { TooltipModule } from 'ng2-tooltip-directive-major-angular-updates';
import { PuzzleUpdateNotificationsState } from '../../../tools/src/lib/store/puzzle-update-notifications.state';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import * as Sentry from '@sentry/angular';

const localStorageImport = [];

if (localStorageAvailable()) {
  localStorageImport.push(
    NgxsStoragePluginModule.forRoot({
      // The keys here are the ones that will be persisted in local storage.
      key: [
        'quizGameStates',
        'leaderboardStates',
        'feedbackStates',
        'puzzleGameState.puzzleGames',
        'puzzleGameState.difficulty',
        'puzzleGameState.language',
        'puzzleGameState.autoZoomEnabled',
        'puzzleGameState.definitionBarEnabled',
        'puzzleGameState.nativeKeyboard',
        'puzzleGameState.keepKeyboardOpen',
        'puzzleGameState.highlightRightAndWrongEnabled',
        'puzzleGameState.showErrorsImmediately',
        'puzzleGameState.highContrastEnabled',
        'puzzleGameState.darkMode',
        'puzzleGameState.letterDeleteRestriction',
        'puzzleGameState.letterOverwriteRestriction',
        'puzzleGameState.wordJumpOnRestriction',
        'puzzleGameState.wordJumpToRestriction',
        'swedishGameStates',
        'swedishGameStatesV2',
        'cookieConsent',
        'puzzleTimeRecordStates',
        'imagePuzzleGameState',
        'sudokuGameStates',
        'hashiGameStates',
        'wordSearchPuzzleState',
        'puzzleUpdateNotifications.updateNotificationDates',
        'leaderboardNicknameState',
      ],
    })
  );
}

export function localStorageAvailable() {
  // Also available in the helper-functions.service.ts
  const test = uuidv1();
  try {
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    console.warn('Local storage is not available. All puzzle progress will be lost if you close this window');
    return false;
  }
}

// if (!environment.firebase || !environment.firebase.apiKey || !environment.firebase.appId) {
//   console.warn('environment.ts needs to include a firebase key with apiKey and appId properties');
// }

@NgModule({
  declarations: [],
  imports: [
    TooltipModule.forRoot({
      'show-delay': 800,
      theme: 'light',
      'z-index': 30000,
      'tooltip-class': 'puzzle-tooltip',
      hideDelayTouchscreen: 0,
      hideDelayAfterClick: 0,
      trigger: 'hover',
      'display-mobile': false,
    }),
    BrowserModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    HttpClientModule,
    RouterModule.forRoot(
      [
        {
          path: '',
          component: AppComponent,
        },
        {
          path: 'project.html',
          component: AppComponent,
        },
      ],
      {}
    ),
    NgxsModule.forRoot(
      [
        PuzzleGameState,
        PuzzleIDState,
        PuzzleZoomState,
        PuzzleCookieConsentState,
        SwedishGameState,
        PuzzleFrameworkConfigState,
        PuzzleTimeRecordState,
        ImagePuzzleGameState,
        SudokuGameState,
        HashiGameState,
        PuzzleNotesModeState,
        WordSearchPuzzleState,
        QuizGameState,
        LegacySwedishGameState,
        PuzzleUpdateNotificationsState,
        LeaderboardState,
        LeaderboardNicknameState,
        FeedbackState,
      ],
      {
        developmentMode: !environment.production,
      }
    ),
    NgxsReduxDevtoolsPluginModule.forRoot(),
    ...localStorageImport,
  ],
  exports: [],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: true,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideIonicAngular({
      mode: 'md',
      innerHTMLTemplatesEnabled: true,
    }),
    { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
    PuzzleStorageEngine,
    {
      provide: STORAGE_ENGINE,
      useClass: PuzzleStorageEngine,
    },
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideFunctions(() => {
      const functions = getFunctions(getApp(), 'europe-west3');
      if (!environment.production) {
        connectFunctionsEmulator(functions, 'localhost', 5001);
      }
      return functions;
    }),
  ],
})
export class AppModule implements DoBootstrap {
  constructor(private injector: Injector) {}

  // bootstrap only when app-root is defined
  public ngDoBootstrap(appRef: ApplicationRef): void {
    registerLocaleData(localeDe, 'de-DE', localeDeExtra);

    if (document.body.classList.contains('nomodule')) {
      return;
    }

    if (document.querySelector('krupion-puzzle-app-root')) {
      appRef.bootstrap(AppComponent);
    } else {
      const injector = this.injector;
      const CustomElement = createCustomElement(AppComponent, { injector });
      customElements.define('krupion-puzzle-webcomponent', CustomElement);
    }
  }
}
